import React, { useMemo, useState } from "react";
import {
  VAULT_AUDIT_LOG_CATEGORIES,
  buildVaultAuditLogSummary,
  exportVaultAuditLogCsv,
  filterVaultAuditLogEntries,
  getVaultAuditLogEntries,
} from "./qmsVaultAuditLogUtils";

function AuditLogDetails({ details }) {
  if (!details) return <span className="text-muted">—</span>;

  if (details.type === "staffList") {
    return (
      <div className="qms-vault-audit__details">
        {details.module ? (
          <div className="qms-vault-audit__details-meta">
            Module: <strong>{details.module}</strong>
            {details.action ? (
              <>
                {" "}
                · Action: <strong>{details.action}</strong>
              </>
            ) : null}
            {details.versionNumber !== "" && details.versionNumber != null ? (
              <>
                {" "}
                · Version: <strong>{details.versionNumber}</strong>
              </>
            ) : null}
          </div>
        ) : null}
        <ul className="qms-vault-audit__details-list mb-0">
          {(details.staff || []).map((person) => (
            <li key={String(person.staffId || person.staffName || person.emailId)}>
              <strong>{person.staffName || "Unknown staff"}</strong>
              {person.emailId ? <span className="text-muted"> · {person.emailId}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (details.type === "object") {
    return (
      <pre className="qms-vault-audit__details-json mb-0">
        {JSON.stringify(details.data, null, 2)}
      </pre>
    );
  }

  return <span className="qms-vault-audit__details-text">{details.text}</span>;
}

function getCategoryLabel(categoryId) {
  return (
    VAULT_AUDIT_LOG_CATEGORIES.find((item) => item.id === categoryId)?.label ||
    categoryId ||
    "—"
  );
}

function AuditLogEntryCard({ entry, isExpanded, onToggleDetails }) {
  const actionTitle = entry.actionLabel || entry.actionName || "—";
  const showDetailsToggle = entry.hasDetails;

  return (
    <article className="qms-vault-audit__card">
      <div className="qms-vault-audit__card-head">
        <div className="qms-vault-audit__card-title-wrap">
          <span
            className={`qms-vault-audit__action-icon is-${entry.categoryMeta.tone}`}
            aria-hidden="true"
          >
            <i className={entry.categoryMeta.icon} />
          </span>
          <h6 className="qms-vault-audit__card-title">{actionTitle}</h6>
        </div>
        <span className={`qms-vault-audit__category is-${entry.categoryMeta.tone}`}>
          {getCategoryLabel(entry.category)}
        </span>
      </div>

      <div className="qms-vault-audit__card-meta">
        <span className="qms-vault-audit__card-meta-item">
          <i className="far fa-clock" aria-hidden="true" />
          {entry.actionDateTime || "—"}
        </span>
        <span className="qms-vault-audit__card-meta-item">
          <i className="far fa-user" aria-hidden="true" />
          {entry.actionBy || "—"}
        </span>
      </div>

      {entry.hasComments ? (
        <div className="qms-vault-audit__card-notes">
          <span className="qms-vault-audit__card-notes-label">Notes</span>
          <p className="qms-vault-audit__card-notes-text">{entry.comments}</p>
        </div>
      ) : null}

      {showDetailsToggle ? (
        <div className="qms-vault-audit__card-footer">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 qms-vault-audit__details-link"
            aria-expanded={isExpanded}
            onClick={onToggleDetails}
          >
            {isExpanded ? "Hide details" : "View details"}
          </button>
        </div>
      ) : null}

      {isExpanded && entry.hasDetails ? (
        <div className="qms-vault-audit__details-block qms-vault-audit__details-block--card">
          <h6>Additional details</h6>
          <AuditLogDetails details={entry.jsonDetails} />
        </div>
      ) : null}
    </article>
  );
}

function QmsVaultAuditLogPanel({ workspaceResponse, loading = false }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const entries = useMemo(
    () => getVaultAuditLogEntries(workspaceResponse),
    [workspaceResponse],
  );

  const filteredEntries = useMemo(
    () => filterVaultAuditLogEntries(entries, { search, category }),
    [category, entries, search],
  );

  const summary = useMemo(() => buildVaultAuditLogSummary(entries), [entries]);

  const visibleFilters = useMemo(
    () =>
      VAULT_AUDIT_LOG_CATEGORIES.filter((item) => {
        if (item.id === "all") return true;
        return (summary.categoryCounts[item.id] || 0) > 0;
      }),
    [summary.categoryCounts],
  );

  const toggleExpanded = (entryId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const handleCategoryClick = (categoryId) => {
    setCategory((current) => (current === categoryId ? "all" : categoryId));
  };

  const handleExport = () => {
    const csv = exportVaultAuditLogCsv(filteredEntries);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "document-audit-log.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch("");
    setCategory("all");
  };

  const hasActiveFilters = Boolean(search.trim()) || category !== "all";

  return (
    <div className="qms-doc-workspace__panel qms-vault-audit">
      <div className="qms-doc-workspace__panel-head qms-vault-audit__head">
        <div>
          <h6 className="qms-doc-workspace__panel-title">Audit log</h6>
          <p className="qms-doc-workspace__panel-desc qms-vault-audit__desc">
            Activity history for this document.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary qms-vault-audit__export"
          disabled={filteredEntries.length === 0}
          onClick={handleExport}
        >
          <i className="fas fa-download me-1" aria-hidden="true" />
          Export CSV
        </button>
      </div>

      <div className="qms-doc-workspace__panel-body qms-vault-audit__body">
        {loading ? (
          <div className="qms-vault-audit__loading">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Loading audit log…</span>
          </div>
        ) : (
          <div className="qms-vault-audit__split">
            <div className="qms-vault-audit__main">
              <div className="qms-vault-audit__list-scroll">
                {filteredEntries.length === 0 ? (
                  <div className="qms-vault-audit__empty">
                    {entries.length === 0
                      ? "No audit entries yet."
                      : "No audit entries match your search or filter."}
                  </div>
                ) : (
                  <div className="qms-vault-audit__list">
                    {filteredEntries.map((entry) => (
                      <AuditLogEntryCard
                        key={entry.id}
                        entry={entry}
                        isExpanded={expandedIds.has(entry.id)}
                        onToggleDetails={() => toggleExpanded(entry.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <aside className="qms-vault-audit__aside">
              <div className="qms-vault-audit__aside-card">
                <h6 className="qms-vault-audit__aside-title">
                  <i className="fas fa-chart-bar" aria-hidden="true" />
                  Summary
                </h6>
                <div className="qms-vault-audit__summary-grid">
                  <div className="qms-vault-audit__summary-item">
                    <span>Total events</span>
                    <strong>{summary.total}</strong>
                  </div>
                  <div className="qms-vault-audit__summary-item">
                    <span>Last activity</span>
                    <strong>{summary.lastActivity}</strong>
                  </div>
                </div>
              </div>

              <div className="qms-vault-audit__aside-card">
                <h6 className="qms-vault-audit__aside-title">
                  <i className="fas fa-filter" aria-hidden="true" />
                  Search &amp; filter
                </h6>
                <label className="qms-vault-audit__aside-label" htmlFor="qms-vault-audit-search">
                  Search
                </label>
                <div className="qms-vault-audit__search-wrap">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    id="qms-vault-audit-search"
                    type="search"
                    className="form-control form-control-sm qms-vault-audit__search"
                    placeholder="Action, user, or comment…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>

                <label className="qms-vault-audit__aside-label">Category</label>
                <div
                  className="qms-vault-audit__filters qms-vault-audit__filters--stacked"
                  role="tablist"
                  aria-label="Audit log filters"
                >
                  {visibleFilters.map((item) => {
                    const count =
                      item.id === "all" ? summary.total : summary.categoryCounts[item.id] || 0;
                    const isActive = category === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`qms-vault-audit__filter${isActive ? " is-active" : ""}`}
                        onClick={() => handleCategoryClick(item.id)}
                        aria-pressed={isActive}
                      >
                        <span className="qms-vault-audit__filter-label">{item.label}</span>
                        <span>{count}</span>
                      </button>
                    );
                  })}
                </div>

                {hasActiveFilters ? (
                  <div className="qms-vault-audit__aside-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary w-100"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : null}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export default QmsVaultAuditLogPanel;
